import { mergeAll } from "ramda";
import { z } from "zod";

import { fetchDocStructure } from "@hsl/core/documents";
import {
    fundsSchema,
    shareClassSchema,
} from "@hsl/core/fund-explorer/metaSchemas";
import {
    fetchFundExplorerData,
    indexData,
} from "@hsl/core/fund-explorer/services";
import { type TopLevelDataObj } from "@hsl/core/fund-explorer/types";
import { type Config, type PageSection } from "@hsl/core/fund-page/schemas";
import {
    getAllDataPartsFromConfig,
    getBannerImage,
    getDataFromPart,
} from "@hsl/core/fund-page/services";
import { messageSchema } from "@hsl/fund-explorer/schemas";
import { generateLocalId } from "@hsl/lgim-explorer/src/store/helpers";

import { config as defaultConfig } from "../config/default.config";
import { processConfig } from "../config/helpers";
import { resolveObject } from "../lib/resolveObject";
import { type InitialData } from "./types";

export function getFundExplorerUrl(config: Config) {
    const { base_url, url, id, audienceId, languageId } = config;
    return `${
        base_url ?? ""
    }${url}/${id}?audience=${audienceId}&language=${languageId}`;
}
export async function fetchTranslations(
    config: Config,
    fundId: number | undefined,
) {
    const { base_url, translations, audienceId, routeId, languageId } = config;
    const translationData = await getDataFromPart({
        baseUrl: base_url,
        partId: translations,
        audienceId,
        routeId: routeId,
        fundID: fundId,
        languageId,
    });
    const { data } = z
        .object({
            data: z.record(z.string()),
        })
        .parse(translationData);
    return data;
}
export async function fetchShareClasses(
    config: Config,
    fundId: number | undefined,
) {
    const { base_url, shareClassesPart, audienceId, routeId, languageId } =
        config;
    const scData = await getDataFromPart({
        baseUrl: base_url,
        partId: shareClassesPart,
        audienceId,
        routeId: routeId,
        fundID: fundId,
        languageId,
    });
    const shareClassList = z
        .object({
            data: z.array(
                z.object({
                    id: z.number(),
                    isin: z.string(),
                    name: z.string(),
                }),
            ),
        })
        .parse(scData);
    return shareClassList;
}
export async function fetchInfoParts(
    config: Config,
    fundId: number | undefined,
) {
    const { base_url, infoPartId, audienceId, routeId, languageId } = config;
    const infoData = await getDataFromPart({
        baseUrl: base_url,
        partId: String(infoPartId),
        audienceId,
        routeId: routeId,
        fundID: fundId,
        languageId,
    });
    // const infoData = {
    //     "info": {
    //         "part": [
    //             {
    //                 "type": "Text",
    //                 "data": "\n    \n        <p data-part-id=\"12800\">\n</p><p>Please note that the name of the Fund was previously the Pre-Retirement Inflation Sensitive Fund. The effective date of the change was 31 December 2022.</p>\n<p></p>\n    \n",
    //                 "partId": 12800
    //             },
    //             {
    //                 "data": {
    //                     "3046": "Ipsum elit enim Lorem eiusmod eu eu. Do est cupidatat laborum dolor dolore. Eu irure do id consectetur ad ad tempor.",
    //                     "3047": "Nulla magna eiusmod sunt incididunt quis labore adipisicing irure do esse occaecat. Pariatur pariatur elit nostrud pariatur culpa fugiat ea enim irure ex ut adipisicing laborum. Consectetur laborum consequat aliqua dolor. Occaecat ex velit irure sint exercitation deserunt adipisicing sunt Lorem. Id officia et sunt dolor tempor id amet consectetur.",
    //                     "3436": "Esse exercitation non veniam aliqua ipsum commodo et reprehenderit sunt ullamco consequat sunt cupidatat occaecat. Ex sit aute anim consequat non velit anim est do commodo do do ad. Fugiat voluptate ut officia ex Lorem irure esse do ipsum magna.",
    //                     "3437": "Magna qui labore excepteur irure sint reprehenderit ea amet cupidatat enim sunt anim id culpa. Esse non laborum aliqua ut dolore. Commodo adipisicing ut cillum sunt tempor ad qui proident aute ea ut velit anim."
    //                 },
    //                 "conf": {},
    //                 "partId": 12781
    //             }
    //         ],
    //         "key": "info",
    //         "list": true
    //     }
    // }
    const parse = messageSchema.safeParse(infoData);
    if (parse.success) {
        return parse.data;
    } else {
        console.log(
            "%cerror FundPage store ",
            "color: red; display: block; width: 100%;",
            `Error parsing info part ${infoPartId}: ${parse.error}`,
        );
    }
}

export async function fetchAllData(config: Config): Promise<InitialData> {
    const processedConfig = processConfig(config);

    const rawData = await fetchFundExplorerData(
        getFundExplorerUrl(processedConfig),
    );
    const mergedConfig = mergeAll([defaultConfig, processedConfig]);

    const indexedData = indexData(rawData);

    const activeFundId = generateLocalId("fund")(processedConfig.fundId);

    if (!(activeFundId in indexedData)) {
        throw new Error(`Fund ${activeFundId} not found in indexedData`);
    }

    const activeFund = fundsSchema.parse({
        type: mergedConfig.productType,
        data: indexedData[activeFundId],
    });

    const { translations, bannerImage, shareClassIds } = await resolveObject({
        translations: fetchTranslations(mergedConfig, activeFund.data.id),
        bannerImage: getBannerImage(mergedConfig).then(
            (banner) => banner ?? "",
        ),
        shareClassIds: fetchShareClasses(mergedConfig, activeFund.data.id).then(
            ({ data }) => data.map((sc) => sc.id),
        ),
    });

    const shareClassLocalIds = shareClassIds.map((id) =>
        generateLocalId("shareclass")(id),
    );

    const shareClassId = config.shareClassId ?? shareClassIds[0];

    if (!shareClassId) throw new Error("No share class ID provided");

    const shareClassLocalId = generateLocalId("shareclass")(shareClassId);

    const activeShareClass = shareClassSchema.parse({
        type: mergedConfig.productType,
        data: indexedData[shareClassLocalId],
    });

    const { pageSections, ribbonDocs } = await resolveObject({
        pageSections: getAllDataPartsFromConfig(
            mergedConfig,
            shareClassId,
        ).then(
            (sections) =>
                sections.filter((x) => x !== undefined) as PageSection[],
        ),
        ribbonDocs: fetchDocStructure(4, {
            base_url: config.base_url,
            audience: config.audienceId,
            share_classes: [activeShareClass?.data.id],
            language: config.languageId,
        }),
    });

    return {
        config: mergedConfig,
        bannerImage,
        activeShareClass,
        activeFund,
        pageSections,
        translations,
        ribbonDocs,
        shareClasses: shareClassLocalIds.map(
            (id) => indexedData[id],
        ) as TopLevelDataObj[],
    };
}
